import { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { API_URL, getAuthHeaders, formatDateTime, feetToMeters } from "../lib/utils";
import { useAuth } from "../context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { MapContainer, Marker, Popup, Circle, Polyline, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import GoogleTileLayer from "../components/Map/GoogleTileLayer";
import { MapPin, Users, RefreshCw, Radio, X } from "lucide-react";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";

// Fix Leaflet default marker icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
});

// Icono para vigilantes activos
const createGuardIcon = (name, photoBase64 = null, color = "#F97316") => {
  const initials = name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  
  const photoHtml = photoBase64 
    ? `<img src="data:image/jpeg;base64,${photoBase64}" style="width: 100%; height: 100%; object-fit: cover; border-radius: 50%;" />`
    : `<div style="
      background-color: ${color};
      color: white;
      width: 100%;
      height: 100%;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 12px;
    ">${initials}</div>`;
  
  return L.divIcon({
    className: "guard-marker",
    html: `<div style="
      width: 40px;
      height: 40px;
      border-radius: 50%;
      border: 3px solid white;
      box-shadow: 0 2px 8px rgba(0,0,0,0.4);
      animation: pulse 2s infinite;
      overflow: hidden;
    ">${photoHtml}</div>
    <style>
      @keyframes pulse {
        0%, 100% { box-shadow: 0 2px 8px rgba(0,0,0,0.4); }
        50% { box-shadow: 0 2px 16px ${color}88; }
      }
    </style>`,
    iconSize: [40, 40],
    iconAnchor: [20, 20],
  });
};

// Componente para ajustar el mapa a los bounds (solo cuando cambia la compañía)
const FitBounds = ({ guards, routes, checkpoints, shouldFit }) => {
  const map = useMap();
  
  useEffect(() => {
    if (!shouldFit || guards.length === 0) return;
    
    const bounds = guards.map((guard) => [guard.latitude, guard.longitude]);
    
    // Agregar checkpoints de las rutas activas
    routes.forEach((route) => {
      if (route.checkpoint_ids && Array.isArray(checkpoints)) {
        route.checkpoint_ids.forEach((cpId) => {
          const cp = checkpoints.find((c) => c.id === cpId);
          if (cp) {
            bounds.push([cp.latitude, cp.longitude]);
          }
        });
      }
    });
    
    if (bounds.length > 0) {
      map.fitBounds(bounds, { padding: [50, 50] });
    }
  }, [shouldFit, guards, routes, checkpoints, map]);
  
  return null;
};

export const ActiveGuards = () => {
  const { t } = useTranslation();
  const { user, can, canManageCompanies } = useAuth();
  const [activeGuards, setActiveGuards] = useState([]);
  const [routes, setRoutes] = useState([]);
  const [checkpoints, setCheckpoints] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState("all");
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [refreshIntervalSeconds, setRefreshIntervalSeconds] = useState(5); // Valor por defecto
  const refreshIntervalRef = useRef(null);
  const [shouldFitBounds, setShouldFitBounds] = useState(true); // Solo ajustar bounds cuando cambia la compañía
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isPhotoModalOpen, setIsPhotoModalOpen] = useState(false);

  const fetchData = async (isInitialLoad = false) => {
    try {
      // Solo mostrar loading en la carga inicial
      if (isInitialLoad) {
      setLoading(true);
      }
      
      const params = new URLSearchParams();
      if (selectedCompany && selectedCompany !== "all") {
        params.append("company_id", selectedCompany);
      }

      const [guardsRes, routesRes, checkpointsRes, companiesRes] = await Promise.all([
        axios.get(`${API_URL}/patrol/active-guards?${params.toString()}`, {
          headers: getAuthHeaders(),
        }),
        axios.get(`${API_URL}/routes`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/checkpoints`, { headers: getAuthHeaders() }),
        axios.get(`${API_URL}/companies`, { headers: getAuthHeaders() }),
      ]);

      setActiveGuards(Array.isArray(guardsRes.data) ? guardsRes.data : []);
      setRoutes(Array.isArray(routesRes.data) ? routesRes.data : []);
      setCheckpoints(Array.isArray(checkpointsRes.data) ? checkpointsRes.data : []);
      setCompanies(Array.isArray(companiesRes.data) ? companiesRes.data : []);
      setLastUpdate(new Date());
    } catch (error) {
      console.error("Error fetching active guards:", error);
      if (isInitialLoad) {
      toast.error("Error al cargar vigilantes activos");
      }
      setActiveGuards([]);
      setRoutes([]);
      setCheckpoints([]);
    } finally {
      if (isInitialLoad) {
      setLoading(false);
      }
    }
  };

  const fetchCompanySettings = async (companyId) => {
    if (!companyId || companyId === "all") {
      // Si no hay compañía seleccionada pero el usuario tiene una, usar esa
      if (user?.company_id) {
        companyId = user.company_id;
      } else {
        return; // No hay compañía para obtener configuración
      }
    }
    
    try {
      const companyRes = await axios.get(`${API_URL}/companies/${companyId}`, {
        headers: getAuthHeaders(),
      });
      const interval = companyRes.data?.map_refresh_interval_seconds ?? 5;
      setRefreshIntervalSeconds(interval);
    } catch (error) {
      console.error("Error al cargar configuración de compañía:", error);
      // Mantener el valor por defecto
    }
  };

  useEffect(() => {
    if (can("active_guards.view")) {
      setShouldFitBounds(true); // Permitir ajuste de bounds cuando cambia la compañía
      fetchData(true); // Carga inicial
      fetchCompanySettings(selectedCompany);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompany]);

  useEffect(() => {
    if (autoRefresh && can("active_guards.view")) {
      // Convertir segundos a milisegundos
      const intervalMs = refreshIntervalSeconds * 1000;
      
      // Limpiar intervalo anterior si existe
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      
      refreshIntervalRef.current = setInterval(() => {
        setShouldFitBounds(false); // No ajustar bounds en actualizaciones automáticas
        fetchData(false); // Actualización sin recargar el mapa
      }, intervalMs);

      return () => {
        if (refreshIntervalRef.current) {
          clearInterval(refreshIntervalRef.current);
        }
      };
    } else {
      // Si autoRefresh está desactivado, limpiar el intervalo
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
        refreshIntervalRef.current = null;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoRefresh, refreshIntervalSeconds]);

  const getRouteForGuard = (guard) => {
    return routes.find((r) => r.id === guard.route_id);
  };

  const getRouteCheckpoints = (route) => {
    if (!route || !route.checkpoint_ids || !Array.isArray(checkpoints)) return [];
    return route.checkpoint_ids
      .map((id) => checkpoints.find((cp) => cp.id === id))
      .filter((cp) => cp != null);
  };

  const getMinutesAgo = (date) => {
    if (!date) return "N/A";
    const now = new Date();
    const then = new Date(date);
    const diffMs = now - then;
    const diffMins = Math.floor(diffMs / 60000);
    if (diffMins < 1) return "Ahora";
    if (diffMins === 1) return "Hace 1 minuto";
    return `Hace ${diffMins} minutos`;
  };

  if (!can("active_guards.view")) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p className="text-slate-600">{t("pages.activeGuards.noPermission")}</p>
      </div>
    );
  }

  // Calcular el centro del mapa basado en los vigilantes activos
  const mapCenter =
    activeGuards.length > 0
      ? [
          activeGuards.reduce((sum, g) => sum + g.latitude, 0) / activeGuards.length,
          activeGuards.reduce((sum, g) => sum + g.longitude, 0) / activeGuards.length,
        ]
      : [18.4655, -66.1057]; // Puerto Rico por defecto

  return (
    <div className="space-y-6" data-testid="active-guards-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">{t("pages.activeGuards.title")}</h1>
          <p className="text-slate-600 mt-1">
            {t("pages.activeGuards.subtitle")}
          </p>
        </div>
        <div className="flex items-center gap-3">
          {lastUpdate && (
            <p className="text-sm text-slate-500">
              {t("pages.activeGuards.lastUpdate")}: {lastUpdate.toLocaleTimeString()}
            </p>
          )}
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              setAutoRefresh(!autoRefresh);
              toast.info(autoRefresh ? t("pages.activeGuards.autoRefreshDisabled") : t("pages.activeGuards.autoRefreshEnabled"));
            }}
          >
            <Radio className={`h-4 w-4 mr-2 ${autoRefresh ? "text-green-600" : ""}`} />
            {autoRefresh ? t("pages.activeGuards.autoUpdating") : t("pages.activeGuards.manual")}
          </Button>
          <Button variant="outline" size="sm" onClick={() => {
            setShouldFitBounds(false); // No ajustar bounds en actualización manual
            fetchData(false);
          }} disabled={loading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
            {t("pages.activeGuards.refresh")}
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <Card>
        <CardContent className="p-4">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-slate-600" />
              <span className="text-sm font-medium text-slate-700">
                {t("pages.activeGuards.activeGuardsCount", { 
                  count: activeGuards.length, 
                  plural: activeGuards.length !== 1 ? "s" : "" 
                })}
              </span>
            </div>
            {canManageCompanies() && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-slate-600">{t("pages.companies.name")}:</label>
                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                  <SelectTrigger className="w-[200px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("pages.routes.allCompanies")}</SelectItem>
                    {Array.isArray(companies) &&
                      companies.map((company) => (
                        <SelectItem key={company.id} value={company.id}>
                          {company.name}
                        </SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Mapa */}
      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <MapPin className="h-5 w-5" />
            {t("pages.activeGuards.realTimeMap")}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="h-[600px] flex items-center justify-center">
              <RefreshCw className="h-8 w-8 animate-spin text-slate-400" />
            </div>
          ) : activeGuards.length === 0 ? (
            <div className="h-[600px] flex flex-col items-center justify-center text-slate-400">
              <Users className="h-16 w-16 mb-4" />
              <p className="text-lg font-medium">{t("pages.activeGuards.noActiveGuards")}</p>
              <p className="text-sm">{t("pages.activeGuards.noActiveGuardsMessage")}</p>
            </div>
          ) : (
            <div className="h-[600px] relative">
              <MapContainer
                key={selectedCompany} // Solo re-renderizar cuando cambia la compañía
                center={mapCenter}
                zoom={13}
                style={{ height: "100%", width: "100%" }}
                scrollWheelZoom={true}
              >
                <GoogleTileLayer />
                <FitBounds 
                  guards={activeGuards} 
                  routes={routes} 
                  checkpoints={checkpoints}
                  shouldFit={shouldFitBounds}
                />

                {/* Dibujar rutas y checkpoints para cada vigilante */}
                {activeGuards.map((guard) => {
                  const route = getRouteForGuard(guard);
                  if (!route) return null;
                  
                  const routeCheckpoints = getRouteCheckpoints(route);
                  
                  return (
                    <div key={guard.user_id}>
                      {/* Línea de la ruta */}
                      {routeCheckpoints.length > 1 && (
                        <Polyline
                          positions={routeCheckpoints.map((cp) => [cp.latitude, cp.longitude])}
                          pathOptions={{
                            color: "#F97316",
                            weight: 3,
                            opacity: 0.5,
                            dashArray: "10, 10",
                          }}
                        />
                      )}

                      {/* Checkpoints de la ruta */}
                      {routeCheckpoints.map((checkpoint, index) => {
                        const isCompleted = Array.isArray(guard.completed_checkpoint_ids) && guard.completed_checkpoint_ids.includes(checkpoint.id);
                        return (
                          <div key={checkpoint.id}>
                            <Circle
                              center={[checkpoint.latitude, checkpoint.longitude]}
                              radius={feetToMeters(checkpoint.radius_feet || 50)}
                              pathOptions={{
                                color: isCompleted ? "#10B981" : "#0F172A",
                                fillColor: isCompleted ? "#10B981" : "#0F172A",
                                fillOpacity: 0.1,
                                weight: 2,
                              }}
                            />
                            <Marker
                              position={[checkpoint.latitude, checkpoint.longitude]}
                              icon={L.divIcon({
                                className: "checkpoint-marker",
                                html: `<div style="
                                  background-color: ${isCompleted ? "#10B981" : "#0F172A"};
                                  color: white;
                                  width: 28px;
                                  height: 28px;
                                  border-radius: 50%;
                                  display: flex;
                                  align-items: center;
                                  justify-content: center;
                                  font-weight: bold;
                                  font-size: 12px;
                                  border: 2px solid white;
                                  box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                                ">${index + 1}</div>`,
                                iconSize: [28, 28],
                                iconAnchor: [14, 14],
                              })}
                            >
                              <Popup>
                                <div className="p-2">
                                  <h3 className="font-bold text-slate-900">{checkpoint.name}</h3>
                                  <p className="text-xs text-slate-600 mt-1">
                                    {isCompleted ? t("pages.activeGuards.completed") : t("pages.activeGuards.pending")}
                                  </p>
                                </div>
                              </Popup>
                            </Marker>
                          </div>
                        );
                      })}
                    </div>
                  );
                })}

                {/* Marcadores de vigilantes */}
                {activeGuards.map((guard) => (
                  <Marker
                    key={guard.user_id}
                    position={[guard.latitude, guard.longitude]}
                    icon={createGuardIcon(guard.user_name, guard.photo_base64)}
                  >
                    <Popup>
                      <div className="p-2 min-w-[380px] max-w-[420px]">
                        <div className="flex items-start gap-3">
                          <div className="flex-shrink-0">
                            {guard.photo_base64 ? (
                              <img
                                src={`data:image/jpeg;base64,${guard.photo_base64}`}
                                alt={guard.user_name}
                                className="w-24 h-32 rounded-lg object-cover border-2 border-orange-500 shadow-md cursor-pointer hover:opacity-80 transition-opacity"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setSelectedPhoto({
                                    photo: guard.photo_base64,
                                    name: guard.user_name
                                  });
                                  setIsPhotoModalOpen(true);
                                }}
                              />
                            ) : (
                              <div className="w-24 h-32 rounded-lg bg-orange-500 flex items-center justify-center text-white font-bold text-xl shadow-md">
                                {guard.user_name
                                  .split(" ")
                                  .map((n) => n[0])
                                  .join("")
                                  .toUpperCase()
                                  .slice(0, 2)}
                              </div>
                            )}
                            <p className="text-xs text-slate-500 mt-1 text-center leading-tight">
                              <strong>{t("pages.activeGuards.lastUpdate")}:</strong><br />
                              {getMinutesAgo(guard.last_update)}
                            </p>
                          </div>
                          <div className="flex-1 min-w-0 space-y-0.5">
                            <h3 className="font-bold text-orange-600 text-sm mb-1 truncate">{guard.user_name}</h3>
                            <p className="text-xs text-slate-600 leading-tight">
                              <strong>{t("nav.routes")}:</strong> <span className="truncate block">{guard.route_name}</span>
                            </p>
                            <p className="text-xs text-slate-600 leading-tight">
                              <strong>{t("pages.companies.name")}:</strong> <span className="truncate block">{guard.company_name}</span>
                            </p>
                            <p className="text-xs text-slate-500 leading-tight">
                              <strong>{t("pages.activeGuards.progress")}:</strong> {guard.completed_checkpoints} / {guard.total_checkpoints} {t("pages.routes.points")}
                            </p>
                            <p className="text-xs text-slate-500 leading-tight">
                              <strong>{t("pages.activeGuards.start")}:</strong> {formatDateTime(guard.session_start)}
                            </p>
                            <p className="text-xs font-mono text-slate-400 mt-1 leading-tight break-all">
                              {guard.latitude.toFixed(6)}, {guard.longitude.toFixed(6)}
                            </p>
                          </div>
                        </div>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Lista de vigilantes activos */}
      {activeGuards.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>{t("pages.activeGuards.activeGuardsDetails")}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {activeGuards.map((guard) => {
                const route = getRouteForGuard(guard);
                return (
                  <div
                    key={guard.user_id}
                    className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                  >
                    <div className="flex items-center gap-4">
                      {guard.photo_base64 ? (
                        <img
                          src={`data:image/jpeg;base64,${guard.photo_base64}`}
                          alt={guard.user_name}
                          className="w-12 h-12 rounded-full object-cover border-2 border-orange-500 cursor-pointer hover:opacity-80 transition-opacity"
                          onClick={() => {
                            setSelectedPhoto({
                              photo: guard.photo_base64,
                              name: guard.user_name
                            });
                            setIsPhotoModalOpen(true);
                          }}
                        />
                      ) : (
                        <div
                          className="w-12 h-12 rounded-full flex items-center justify-center text-white font-bold text-sm"
                          style={{ backgroundColor: "#F97316" }}
                        >
                          {guard.user_name
                            .split(" ")
                            .map((n) => n[0])
                            .join("")
                            .toUpperCase()
                            .slice(0, 2)}
                        </div>
                      )}
                      <div>
                        <h3 className="font-semibold text-slate-900">{guard.user_name}</h3>
                        <p className="text-sm text-slate-600">
                          {guard.route_name} • {guard.company_name}
                        </p>
                        <p className="text-xs text-slate-500 mt-1">
                          {t("pages.activeGuards.updated")}: {getMinutesAgo(guard.last_update)}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <div className="text-right">
                        <Badge variant="outline" className="mb-1">
                          {guard.completed_checkpoints} / {guard.total_checkpoints} {t("pages.activeGuards.points")}
                        </Badge>
                        <p className="text-xs text-slate-500">
                          {t("pages.activeGuards.start")}: {new Date(guard.session_start).toLocaleTimeString()}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Modal de Foto del Vigilante */}
      <Dialog open={isPhotoModalOpen} onOpenChange={setIsPhotoModalOpen}>
        <DialogContent className="max-w-4xl w-[90vw] bg-white p-0 overflow-hidden">
          <DialogHeader className="p-6 pb-4">
            <DialogTitle className="text-2xl font-bold text-slate-900">
              {selectedPhoto?.name || "Vigilante"}
            </DialogTitle>
          </DialogHeader>
          <div className="p-6 pt-0">
            {selectedPhoto?.photo ? (
              <div className="flex items-center justify-center bg-slate-100 rounded-lg overflow-hidden">
                <img
                  src={`data:image/jpeg;base64,${selectedPhoto.photo}`}
                  alt={selectedPhoto.name}
                  className="max-w-full max-h-[70vh] object-contain"
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-64 bg-slate-100 rounded-lg">
                <p className="text-slate-400">No hay foto disponible</p>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

